import { useGetList, useTranslate, type Identifier } from "ra-core";
import type { ReactNode } from "react";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { Card, CardContent } from "@/components/ui/card";
import { useDateFormat } from "@/hooks/use-date-format";
import { useMoneyFormat } from "@/hooks/use-money-format";
import { cn } from "@/lib/utils";

import type { Sale } from "../../atomic-crm/types";
import {
  formatPersonName,
  getNameInitials,
} from "../../atomic-crm/misc/personName";

/** Coloured dot + translated label for a vocabulary value. */
export const StatusBadge = ({
  choice,
  className,
}: {
  choice?: { name: string; color?: string } | null;
  className?: string;
}) => {
  const translate = useTranslate();
  if (!choice) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium whitespace-nowrap",
        className,
      )}
    >
      {choice.color && (
        <span
          className="size-2 rounded-full"
          style={{ backgroundColor: choice.color }}
        />
      )}
      {translate(choice.name)}
    </span>
  );
};

export const ProgressRing = ({
  value,
  className,
}: {
  value: number;
  className?: string;
}) => {
  const clamped = Math.max(0, Math.min(100, value));
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg
      viewBox="0 0 36 36"
      className={cn("size-9 shrink-0 -rotate-90", className)}
      aria-hidden
    >
      <circle
        cx="18"
        cy="18"
        r={radius}
        fill="none"
        stroke="var(--muted)"
        strokeWidth="4"
      />
      <circle
        cx="18"
        cy="18"
        r={radius}
        fill="none"
        stroke="var(--chart-3)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference - (clamped / 100) * circumference}
      />
    </svg>
  );
};

export const ProgressBar = ({
  value,
  className,
  showLabel = true,
}: {
  value: number;
  className?: string;
  showLabel?: boolean;
}) => {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  const color =
    clamped === 100
      ? "bg-success"
      : clamped >= 50
        ? "bg-primary"
        : "bg-warning";
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all", color)}
          style={{ width: `${clamped}%` }}
        />
      </div>
      {showLabel && (
        <span className="w-9 text-right text-xs tabular-nums text-muted-foreground">
          {clamped}%
        </span>
      )}
    </div>
  );
};

/** All CRM users, cached once (Jorge and Alberto). */
export const useSales = () =>
  useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "first_name", order: "ASC" },
    },
    { staleTime: 5 * 60 * 1000 },
  );

export const saleName = (sale?: Sale | null) => formatPersonName(sale);

export const saleInitials = (sale?: Sale | null) => getNameInitials(sale);

export const SaleAvatar = ({
  saleId,
  className,
}: {
  saleId?: Identifier | null;
  className?: string;
}) => {
  const { data: sales } = useSales();
  const translate = useTranslate();
  const sale = sales?.find((s) => s.id === saleId);
  return (
    <span
      title={sale ? saleName(sale) : translate("hub.common.nobody")}
      className={cn(
        "inline-flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
        sale ? "bg-primary text-primary-foreground" : "border border-dashed",
        className,
      )}
    >
      {saleInitials(sale)}
    </span>
  );
};

export const SaleInput = ({
  source,
  label,
}: {
  source: string;
  label?: string;
}) => (
  <ReferenceInput source={source} reference="sales">
    <SelectInput
      label={label}
      optionText={(sale: Sale) => saleName(sale)}
      emptyText="hub.common.nobody"
      parse={emptyToNull}
    />
  </ReferenceInput>
);

/** Empty select values ("") must reach bigint/date columns as null. */
export const emptyToNull = (value: unknown) => (value === "" ? null : value);

export const useFormatMoney = () => {
  const { formatMoney } = useMoneyFormat();
  return (value?: number | null) =>
    value == null ? "—" : formatMoney(Number(value));
};

export const useFormatDate = () => {
  const { formatDate } = useDateFormat();
  // "31 oct 2026" by default; times always on a 24-hour clock. Plain dates
  // (YYYY-MM-DD) are read as local dates, not UTC midnight.
  return (value?: string | null, options?: Intl.DateTimeFormatOptions) =>
    formatDate(value, options) || "—";
};

export const todayIso = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const isOverdue = (date?: string | null, done = false) =>
  !done && !!date && date.slice(0, 10) < todayIso();

/** Page shell for hub forms and detail pages. */
export const HubPage = ({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn("mx-auto mt-4 w-full max-w-5xl", className)}>
    {(title || actions) && (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        {typeof title === "string" ? (
          <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
        ) : (
          title
        )}
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      </div>
    )}
    {children}
  </div>
);

export const SectionCard = ({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <Card className={className}>
    <CardContent className="space-y-3">
      {(title || actions) && (
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
          <div className="flex items-center gap-2">{actions}</div>
        </div>
      )}
      {children}
    </CardContent>
  </Card>
);
